
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { RadioQuestion as RadioQuestionType } from "./types";
import { QuestionTooltip } from "./QuestionTooltip";
import { OtherTextInput, isOtherValue } from "./OtherTextInput";
import { AnswerData } from "./answerTypes";

interface RadioQuestionProps {
  question: RadioQuestionType;
  value: string;
  otherText?: string;
  onChange: (questionId: string, value: string) => void;
  allAnswers?: AnswerData;
}

/**
 * Resolves the effective question title for special questions
 * that need dynamic text based on other answers.
 */
function resolveQuestionTitle(question: RadioQuestionType, allAnswers?: AnswerData): string {
  if (question.id === 'dealer_succession_program' && allAnswers) {
    const primary = allAnswers['exit_route_primary'];
    const fallback = allAnswers['exit_route_fallback'];
    
    if (primary === 'dealer_program') {
      return "You indicated Dealer Succession Program as your primary succession approach. Are you formally registered in it?";
    }
    if (fallback === 'dealer_program' && primary !== 'dealer_program') {
      return "You indicated Dealer Succession Program as your fallback option. Are you formally registered in it?";
    }
  }
  return question.title;
}

export function RadioQuestion({
  question,
  value,
  otherText = "",
  onChange,
  allAnswers
}: RadioQuestionProps) {
  const hasOtherOption = question.options?.some(o => isOtherValue(o.value));
  const isOtherSelected = isOtherValue(value);

  // For exit_route_fallback, the primary selection is disabled
  const primaryRouteValue = (question.id === 'exit_route_fallback' && allAnswers)
    ? (allAnswers['exit_route_primary'] as string | undefined)
    : undefined;

  const effectiveTitle = resolveQuestionTitle(question, allAnswers);

  return (
    <div className="space-y-8 w-full max-w-4xl mr-auto py-4 min-h-[400px] text-left">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-3">
          {effectiveTitle}
          {question.tooltip && <QuestionTooltip tooltip={question.tooltip} />}
        </h2>
        
        {question.description && <p className="text-muted-foreground">{question.description}</p>}
      </div>
      
      <RadioGroup value={value || ""} className="grid grid-cols-1 gap-3 mt-6" onValueChange={value => onChange(question.id, value)}>
        {question.options?.map(option => {
          const isDisabledAsPrimary = primaryRouteValue === option.value;
          const displayLabel = isDisabledAsPrimary
            ? `${option.label} (Selected as primary)`
            : option.label;

          return (
            <div key={option.value}>
              <div 
                onClick={() => {
                  if (!isDisabledAsPrimary) {
                    onChange(question.id, option.value);
                  }
                }} 
                className={`option-card flex items-center space-x-3 p-4 border hover:bg-muted/50 transition-all w-full max-w-2xl px-[10px] rounded-md mx-[10px] py-[6px] ${
                  isDisabledAsPrimary
                    ? 'border-muted bg-muted/30 opacity-60 cursor-not-allowed'
                    : 'border-border hover:border-primary cursor-pointer'
                }`}
              >
                <RadioGroupItem
                  value={option.value}
                  id={option.value}
                  className="transition-all"
                  disabled={isDisabledAsPrimary}
                />
                <Label
                  htmlFor={option.value}
                  className={`text-base font-medium ${isDisabledAsPrimary ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}`}
                >
                  {displayLabel}
                </Label>
              </div>
              {hasOtherOption && isOtherValue(option.value) && isOtherSelected && (
                <OtherTextInput
                  questionId={question.id}
                  value={otherText}
                  onChange={onChange}
                  placeholder="Please describe your concern..."
                />
              )}
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
}
